import React, { useState } from "react";
import { RCCAlert, RCCBadge, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import Accordion, { CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER } from "src/components/Accordion";
import Loader from "src/components/Loader";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableHandle from "src/components/Sortable/Handle";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import formatNumber from "src/utils/formatNumber";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { simplePermissionType } from "src/components/Permissions/interface";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { ContainerAccordion } from "../styles";
import { IMatba001Form, IMatba002, IMatba003 } from "../interface";
import { IRefModalLevel1 } from "./ModalLevel1";
import { IRefModalLevel2 } from "./ModalLevel2";
import ListOfLevel2 from "./listOfLevel2";

export interface IListOfLevel1{
    analysisData: IMatba001Form;
    level1Data: IMatba002;
    refModalLevel1: React.RefObject<IRefModalLevel1>;
	refModalLevel2: React.RefObject<IRefModalLevel2>;
}

const ListOfLeve1: React.FC<IListOfLevel1> = ({
	analysisData,
	level1Data,
	refModalLevel1,
	refModalLevel2
}) => {

	const { translate, currentLanguage } = useTranslation();
	const {addToast} = RCCHookToast();

	const [expandedAccordion, setExpandedAccordion] = useState(false);
	const [ data, setData] = useState<IMatba003[] | null>(null);
	const [ tempData , setTempData ] = useState<IMatba003[] | null>(null);
	const [loading, setIsLoading] = useState(true);
	const [waitingForResponse, setWaitingForResponse] = useState(false);
	const [evaluation, setEvaluation] = useState(level1Data.chartEvaluation);

	const loadData = async () => {
		try{
			setIsLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "Analysis/ListLevel2",
				urlParameters: {
					id: level1Data.maa002Id
				}
			});

			setData(response.data);
			setIsLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setIsLoading(false);
		}
	};

	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);
		if(!expanded)
			return;
		loadData();
	};

	const onCallbackEvaluation = async () => {
		try{
			const response = await RCCServicesApi.getRequest({
				method: "Analysis/SelectLevel1",
				urlParameters: {
					id: level1Data.maa002Id
				}
			});

			setEvaluation(response.data.chartEvaluation);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
		}
	};

	const onCallbackAddLevel2 = (level2: IMatba003) => {
		if(data !== null){
			setData([level2, ...data]);
		}else{
			setData([level2]);
		}
	};

	const onCallbackChangeLevel2 = (level2: IMatba003) => {
		setData(data && data.map((item) => {
			if(item.maa003Id === level2.maa003Id){
				return {
					...level2
				};
			}
			return item;
		}));
	};

	const onCallbackDeleteLevel2 = (id: number) => {
		setData(data && data.filter((item) => item.maa003Id !== id));
		onCallbackEvaluation();
	};

	const queryReorderLevel2 = async (levels2: any[]) => {

		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "Analysis/UpdateOrderLevel2",
				body: {
					rows: levels2
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: translate("{{label}} movido(a) com sucesso", {label: analysisData.maa001Label2}),
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setData(tempData);
			standardErrorMessages(err, translate("Erro ao mover {{label}}", {label: analysisData.maa001Label2}), addToast);
			setWaitingForResponse(false);
			return false;
		}
	};

	return (
		<>
			<ContainerAccordion>
				<div>
					<Accordion 
						expanded={expandedAccordion}
						onChange={(e, expanded) => {e.stopPropagation();  handleAccordion(expanded); }}
						header={
							<>
								<RCCGrid 
									container
									item
									justifyContent="space-between"
									alignItems="center"
									xs={12}
								>
									<RCCGrid
										item
										md={9}
										xs={12}
										styleInline="display: flex"
									>
										<span>{level1Data.maa002Descricao}</span>
										{
											level1Data.maa002Explicacao &&
											<RCCPopover
												position="bottom"
												header={{title:translate("Explicação")}}
												body={{text:`${level1Data.maa002Explicacao}`}}
											>
												<RCCIcon
													style={{marginLeft: 5}}
													name="infoCircle"
													color="grey"
												/>
											</RCCPopover>
										}
										
									</RCCGrid>
									<RCCGrid
										container 
										item
										justifyContent="flex-end"
										md={3}
										xs={12}
										className={CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER}
									>
										{
											analysisData.maa001Graficoradar === "S" && 
											<RCCGrid alignItems="flex-end" style={{display: "flex"}} title={`${translate("Avaliação Gráfico")}  (G x U x T) / 25`}>
												<span><b>{translate("Gráfico")}:</b> </span>
												<RCCBadge color="blue"
													styleInline="font-size: 0.83rem; height: 15px"
												>
													{formatNumber(evaluation, currentLanguage)}
												</RCCBadge>
											</RCCGrid>
										}
										{
											analysisData.userPermission > Number(simplePermissionType.VIEW) &&
											<>
												<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
													<RCCTooltip
														text={translate("Adicionar {{label}}", {label: analysisData.maa001Label2})}
														color="purple"
													>
														<RCCButton
															icon={{name: "plus", size: 12}}
															outlined
															color="purple"
															size="xs"
															layout="isRounded"
															onClick={(e) => {e.stopPropagation(); refModalLevel2.current?.actionButtonAdd(level1Data.maa002Id, analysisData.maa001Label2, onCallbackAddLevel2);}}
														/>
													</RCCTooltip>
													<RCCTooltip
														text={translate("Editar {{label}}", {label: analysisData.maa001Label1})}
														color="grey"
													>
														<RCCButton
															icon={{name: "pencil", size: 12}}
															outlined
															color="grey"
															size="xs"
															layout="isRounded"
															onClick={(e) => {e.stopPropagation();  refModalLevel1.current?.actionButtonChange(level1Data.maa002Id, analysisData.maa001Label1); }}
														/>
													</RCCTooltip>
												</CheckPermission>
												<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
													<RCCTooltip
														text={translate("Excluir {{label}}", {label: analysisData.maa001Label1})}
														color="red"
													>
														<RCCButton
															icon={{name: "trash", size: 12}}
															outlined
															color="red"
															size="xs"
															layout="isRounded"
															onClick={(e) => {e.stopPropagation(); refModalLevel1.current?.actionButtonDelete(level1Data.maa002Id, level1Data.maa002Descricao);}}
														/>
													</RCCTooltip>
												</CheckPermission>
												<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
													<SortableHandle>
														<RCCTooltip 
															color="grey"
															text={translate("Mover {{label}}", {label: analysisData.maa001Label1})}
														>
															<RCCButton
																color="grey"
																outlined
																size="xs"
																icon={{
																	name: "arrows"
																}}
																layout="isRounded"
																onClick={(e) => e.stopPropagation()}
															/>
														</RCCTooltip>	
													</SortableHandle>
												</CheckPermission>
											</>
										}
									</RCCGrid>
								</RCCGrid>
							</>}
					>
						{loading ? (
							<Loader />
						) : (
							<>
								{!data || data.length === 0 ? 
									(
										<RCCGrid 
											container 
											styleInline="margin: 10px"
										>
											<RCCAlert 
												title={translate("Atenção")}
												description={translate("Nenhum(a) {{label}} encontrado(a)", {label: analysisData.maa001Label2})}
											/>
										</RCCGrid>
									) : (
										<>
											<RCCGrid 
												container
												item
												xs={12}
											>
												<RCCGrid 
													container
													item
													xs={12}
												>
													<ul className="ul-businessModel-head">
														<li className="businessModel-head">
															<RCCGrid 
																container
																item
																xs={12}
															>
																<span className="head-level-1">{analysisData.maa001Label2}</span>
															</RCCGrid>
														</li>
													</ul>
												</RCCGrid>
												<RCCGrid 
													container
													item
													xs={12}
												>
													<SortableContainer
														onDragStart={() => {
															setTempData(data);
														}}
  													onDragEnd={(activeIdX, overId) => {
															if(activeIdX === overId)
																return; 	

															const activeIndex = data.findIndex((checks) => checks.id === activeIdX);
															const overIndex = data.findIndex((checks) => checks.id === overId);

															const orderedItems = arrayMove(data, activeIndex, overIndex);

															setData(orderedItems);

															const rowsOrdered: any[] = [];

															orderedItems.forEach((item: any, index) => {
																const row = {
																	maa003Id: item.maa003Id,
																	maa003Ordem: index
																};
																rowsOrdered.push(row);
															});

															queryReorderLevel2(rowsOrdered);
														}}
														items={data.map((line) => ({
															...line,
															id: `${line.maa003Id.toString()}`
														}))}
														modifiers={[restrictToVerticalAxis]}
  												>
														{data?.map((level2) => (
															<div key={`container-question-${level2.maa003Id}`} style={{ width: "100%" }}>
																<SortableItem 
																	id={level2.maa003Id.toString()} 
																	key={level2.maa003Id} 
																	disabled={waitingForResponse}
																>
																	<ListOfLevel2
																		analysisData={analysisData}
																		level2Data={level2}
																		refModalLevel2={refModalLevel2}
																		onCallbackEvaluation={onCallbackEvaluation}
																		onCallbackChangeLevel2={onCallbackChangeLevel2}
																		onCallbackDeleteLevel2={onCallbackDeleteLevel2}
																	/>
																</SortableItem>
															</div>
														))}
													</SortableContainer>
												</RCCGrid>
											</RCCGrid>
										</>
									)
								}
							</>
						)}	
					</Accordion>
				</div>
			</ContainerAccordion>

		</>
	);
};


export default ListOfLeve1;
